import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { makeAutoObservable } from 'mobx';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import moment from 'moment-timezone';
import { AntTextArea } from 'controls/react/ant/antInput';
import ScheduleWindow from 'controls/react/form/scheduleWindow';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { AntSelect } from 'controls/react/ant/ant-select/antSelect';
import { FormEntryNew } from '../../formEntryNew';
import { FormBuilderContext } from '../formBuilderContext';
import { FormBuilderStore } from '../formBuilderStore';
import { Property } from '../models';
import b_ from 'b_';
import { fieldNamesTextValue } from 'controls/react/ant/ant-select/types';
import { useStore } from 'core/react/useStore';
import { MobxManager } from 'framework/mobx-integration';

const defaultSchedule = (timezone: string) => {
	const startDate = moment(new Date()).format('YYYYMMDDTHHmm00');

	return `BEGIN:VEVENT\r\nDTSTART;TZID=${timezone}:${startDate}\r\nRRULE:FREQ=HOURLY;INTERVAL=1\r\nEND:VEVENT`;
};

export const b = b_.with('ce-scheduler');

enum SchedulerMode {
	INTERVAL = 'INTERVAL',
	SCHEDULE = 'SCHEDULE',
}

interface ModeItem {
	text: string;
	value: string;
}

import { i18n } from 'core/localization';
import { isNumeric } from 'tools/helpers/isNumeric';

const getMode = (value: string | number) => (isNumeric(value) ? SchedulerMode.INTERVAL : SchedulerMode.SCHEDULE);

// MobX Store
class CEScheduleStore {
	showWindow = false;
	mode: SchedulerMode = SchedulerMode.INTERVAL;
	waitingForDataSource = false;
	mobx = new MobxManager();

	constructor(
		private property: Property,
		private formStore: FormBuilderStore,
	) {
		makeAutoObservable(this);
		if (!this.value) {
			this.value = this.defaultValue;
			this.waitingForDataSource = this.dataSourceValue == null;
		}
		this.mode = getMode(this.value);

		this.mobx.reaction(
			() => this.dataSourceValue,
			(dataSourceValue) => {
				if (dataSourceValue == null || !this.waitingForDataSource) {
					return;
				}
				this.waitingForDataSource = false;
				this.value = dataSourceValue.toString();
				this.mode = getMode(dataSourceValue);
			},
		);
	}

	get dataSourceValue() {
		return this.formStore.dataSources.get(this.property.id) as string | number | undefined;
	}

	get loading() {
		return this.formStore.propertyLoading(this.property);
	}

	get isInterval(): boolean {
		return isNumeric(this.value);
	}

	setShowWindow = (show: boolean) => {
		this.showWindow = show;
	};

	handleSave = (value: string) => {
		this.setShowWindow(false);
		this.onChange(value);
	};

	handleCancel = () => {
		this.setShowWindow(false);
	};

	handleOpen = () => {
		if (this.formStore.propertyDisabled(this.property)) {
			return;
		}
		this.setShowWindow(true);
	};

	get defaultValue() {
		return this.dataSourceValue?.toString() ?? '30';
	}

	handleModeChange = (newMode: SchedulerMode) => {
		this.mode = newMode;
		if (newMode === SchedulerMode.INTERVAL) {
			this.onChange(this.defaultValue);
		} else {
			this.onChange(defaultSchedule(this.formStore.timezone));
		}
	};

	onChange = (value: string) => {
		this.waitingForDataSource = false;
		this.formStore.onPropertyValueChange(this.property, value);
	};

	onIntervalChange = (value: number) => {
		this.onChange('' + value);
	};

	get modes(): ModeItem[] {
		const modes: ModeItem[] = [{ text: this.property.intervalCaption, value: SchedulerMode.INTERVAL }];
		if (this.property.interval != true) {
			modes.push({ text: this.property.scheduleCaption, value: SchedulerMode.SCHEDULE });
		}
		return modes;
	}

	get value() {
		return this.formStore.value[this.property.id] as string | number;
	}

	set value(value: string | number) {
		this.formStore.onPropertyValueChange(this.property, value);
	}

	destroy() {
		this.mobx.destroy();
	}
}

// Observer component
export const CESchedule = observer(({ property }: { property: Property }) => {
	const formStore = React.useContext(FormBuilderContext);
	const store = useStore(() => new CEScheduleStore(property, formStore), { deps: [property] });
	const disabled = formStore.propertyDisabled(property);
	const classes = classnames({
		'advanced-field': property.advanced,
	});

	return (
		<>
			<FormEntryNew
				label={i18n('Type')}
				className={classes}
				model={store}
				modelField="mode"
				onChange={store.handleModeChange}
				headerAdditionalContent={store.loading ? <LoadingOutlined /> : null}
			>
				<AntSelect options={store.modes} fieldNames={fieldNamesTextValue} disabled={disabled} />
			</FormEntryNew>

			{store.isInterval && (
				<FormEntryNew label={i18n('Interval')} className={classes} model={store} modelField="value">
					<AntInputNumber onChange={store.onIntervalChange} disabled={disabled} />
				</FormEntryNew>
			)}

			{!store.isInterval && (
				<FormEntryNew
					label={i18n('RRULE')}
					headerAdditionalContent={<EditOutlined onClick={store.handleOpen} />}
					model={store}
					modelField={'value'}
				>
					<AntTextArea
						onChange={store.onChange}
						title={store.value}
						autoSize={{ minRows: 1, maxRows: 4 }}
						disabled={disabled}
					/>
				</FormEntryNew>
			)}
			{store.showWindow && (
				<ScheduleWindow
					rule={store.value}
					onSave={store.handleSave}
					onCancel={store.handleCancel}
					timezone={formStore.timezone}
				/>
			)}
		</>
	);
});

export default CESchedule;
